import { parseJson } from 'legacy/lut/classes/helpers/JsonHelper'
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Tabs } from 'cng-common/components/tabs'
import { useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { RankIcon } from 'partner360/components/partnerRanks/components/rankIcon/RankIcon'
import { shouldUseFlagRequest } from 'partner360/features/topPartnersManagement/helpers/shouldUseFlagRequest'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import { IPartnerWithFlagRequests } from 'partner360/models/partnerFlagRequests/IPartnerWithFlagRequests'
import { TFlagChangeRequest } from 'partner360/models/partnerFlagRequests/TFlagChangeRequest'
import { PartnerBitFlag } from 'partner360/models/report/PartnerBitFlag'

import { TopPartnersManagementCard } from './components/topPartnersManagementCard/TopPartnersManagementCard'
import { TopPartnersManagementChart } from './components/topPartnersManagementChart/TopPartnersManagementChart'
import { RequestsStatisticFilter } from './components/topPartnersManagementFilterArea/components/requestsStatisticFilter/RequestsStatisticFilter'
import { TopPartnersManagementFilterArea } from './components/topPartnersManagementFilterArea/TopPartnersManagementFilterArea'
import { TopPartnersManagementTable } from './components/topPartnersManagementTable/TopPartnersManagementTable'
import {
  INCLUDE_MARKET_FLAGS,
  TOP_PARTNERS_MANAGEMENT_FORM_KEY,
  TOP_PARTNERS_MANAGEMENT_INITIAL_FORM_DATA,
} from './configs'
import { buildFlagConfigs } from './helpers/buildFlagConfigs'
import { getFlagRequestDirection } from './helpers/getFlagRequestDirection'
import { hasFlag } from './helpers/hasFlag'
import { isActiveFlagRequest } from './helpers/isActiveFlagRequest'
import { IPreparedTopPartner } from './models'
import { TRANSLATION_KEYS } from './translations'

import styles from './TopPartnersManagement.module.scss'

export interface ITopPartnersPbmAssignment {
  PartnerId: string
  ProgramType?: string
  PbmEmail?: string
}

export interface ITopPartnersManagementProps {
  fYear?: number
  partnerFlags: IP360PartnerFlags[]
  partners: IPartnerWithFlagRequests[]
  setPartners: Dispatch<SetStateAction<IPartnerWithFlagRequests[]>>
  approvers: string[]
  pbmAssignments?: ITopPartnersPbmAssignment[]
  formKey?: string
}

export const TopPartnersManagement = ({
  fYear,
  partnerFlags,
  partners,
  setPartners,
  approvers,
  pbmAssignments = [],
  formKey = TOP_PARTNERS_MANAGEMENT_FORM_KEY,
}: ITopPartnersManagementProps) => {
  const topFlags = useMemo(
    () =>
      partnerFlags.filter(
        (flag) =>
          (flag.IsTopManagement === 1 || flag.IsStrategic === 1) &&
          flag.RTM === 'Reseller' &&
          (INCLUDE_MARKET_FLAGS || flag.IsTopMarket !== 1)
      ),
    [partnerFlags]
  )

  const flagTabs = useMemo(() => {
    const groups = [
      {
        key: 'top-oph',
        title: 'Top OPH',
        bits: [PartnerBitFlag.TopOPHWW, PartnerBitFlag.TopOPHMarket],
      },
      {
        key: 'top-bps',
        title: 'Top BPS',
        bits: [PartnerBitFlag.TopBPSWW, PartnerBitFlag.TopBPSMarket],
      },
    ]
    const partnerRtms = new Set(partners.map((partner) => partner.RTM))
    const additionalFlags = partnerFlags.filter(
      (flag) =>
        (flag.RTM === 'Distributor' || flag.RTM === 'Retail') &&
        partnerRtms.has(flag.RTM) &&
        flag.Type === 'Class' &&
        flag.IsTopManagement === 1
    )
    const groupedBits = new Set(groups.flatMap((group) => group.bits))
    return [
      ...groups
        .map((group) => ({
          key: group.key,
          title: group.title,
          flags: partnerFlags.filter(
            (flag) =>
              flag.RTM === 'Reseller' &&
              flag.Type === 'Class' &&
              group.bits.includes(flag.BitValue) &&
              (INCLUDE_MARKET_FLAGS ||
                (flag.BitValue !== PartnerBitFlag.TopOPHMarket &&
                  flag.BitValue !== PartnerBitFlag.TopBPSMarket))
          ),
        }))
        .filter((group) => group.flags.length > 0),
      ...topFlags
        .filter((flag) => !groupedBits.has(flag.BitValue))
        .map((flag) => ({ key: flag.FlagKey, title: flag.Title, flags: [flag] })),
      ...additionalFlags.map((flag) => ({ key: flag.FlagKey, title: flag.Title, flags: [flag] })),
    ].sort((a, b) => {
      const getSortOrder = (flags: IP360PartnerFlags[]) =>
        Math.min(
          ...flags.map((flag) => {
            const value = Number(flag.SortOrder)
            return flag.SortOrder != null && String(flag.SortOrder).trim() && Number.isFinite(value)
              ? value
              : Infinity
          })
        )

      return getSortOrder(a.flags) - getSortOrder(b.flags)
    })
  }, [partnerFlags, topFlags, partners])

  const [selectedTab, setSelectedTab] = useState(0)
  const activeTab = Math.max(0, Math.min(selectedTab, flagTabs.length - 1))
  const initialFormData = TOP_PARTNERS_MANAGEMENT_INITIAL_FORM_DATA

  const [{ formData: filtersFormData, modifiedFields }] = useGetFormData(formKey, initialFormData)

  const partnerIdSet = useMemo(() => {
    return new Set(filtersFormData.partnerId)
  }, [filtersFormData.partnerId])

  const countryCodeSet = useMemo(() => {
    return new Set(filtersFormData.countryCode)
  }, [filtersFormData.countryCode])

  const usedFlags = useMemo(() => flagTabs[activeTab]?.flags ?? [], [flagTabs, activeTab])

  const flagConfigs = useMemo(() => {
    return buildFlagConfigs(
      usedFlags.map((flag) => ({ bitValue: flag.BitValue, label: flag.Title }))
    )
  }, [usedFlags])

  const preparedPartners = useMemo<IPreparedTopPartner[]>(() => {
    const filteredPartners = partners.filter((partner) => {
      const isMatchedByPartnerId = !partnerIdSet.size || partnerIdSet.has(partner.PartnerId)
      const isMatchedByCountryCode = !countryCodeSet.size || countryCodeSet.has(partner.CountryCode)

      return isMatchedByPartnerId && isMatchedByCountryCode
    })

    return filteredPartners.map((partner) => ({
      ...partner,
      ParsedRequest: partner.Request ? parseJson<TFlagChangeRequest>(partner.Request) : null,
      RequestCreatedDate: partner.RequestCreated ? new Date(partner.RequestCreated) : null,
    }))
  }, [partners, partnerIdSet, countryCodeSet])

  const hasPartnerFlagForFilter = useCallback(
    (partner: IPreparedTopPartner, flag: IP360PartnerFlags) => {
      const inBitClass = Boolean(flag.BitValue && hasFlag(partner.BitClass, flag.BitValue))
      const inRequest =
        partner.ParsedRequest?.[flag.FlagKey] === 1 && shouldUseFlagRequest(partner.RequestStatus)
      return partner.RTM === flag.RTM && (inBitClass || inRequest)
    },
    []
  )

  const getDataByFlags = useCallback(
    (partnersData: IPreparedTopPartner[], flags: IP360PartnerFlags[]) => {
      if (!flags.length) {
        return []
      }

      return partnersData.filter((partner) =>
        flags.some((flag) => hasPartnerFlagForFilter(partner, flag))
      )
    },
    [hasPartnerFlagForFilter]
  )

  const dataForTopPartners = useMemo(() => {
    return getDataByFlags(preparedPartners, usedFlags)
  }, [preparedPartners, getDataByFlags, usedFlags])

  const requestsForTab = useMemo(
    () =>
      dataForTopPartners.filter(
        (partner) =>
          isActiveFlagRequest(partner.RequestStatus) &&
          usedFlags.some((flag) => getFlagRequestDirection(partner, flag) !== null)
      ),
    [dataForTopPartners, usedFlags]
  )

  const requestsForTabSet = useMemo(() => new Set(requestsForTab), [requestsForTab])

  const filteredDataForTable = useMemo<IPreparedTopPartner[]>(() => {
    const flagOffering = usedFlags[0]?.Offering

    return dataForTopPartners
      .filter((partner) => {
        if (filtersFormData.requestStatus && !requestsForTabSet.has(partner)) {
          return false
        }
        const isMatchedAllRequestStatus =
          filtersFormData.requestStatus == 'All' &&
          (partner.RequestStatus === 'New' || partner.RequestStatus === 'Wip')
        return (
          !filtersFormData.requestStatus ||
          partner.RequestStatus === filtersFormData.requestStatus ||
          isMatchedAllRequestStatus
        )
      })
      .map((partner) => ({
        ...partner,
        PbmEmail: pbmAssignments.find(
          (el) => el.ProgramType === flagOffering && el.PartnerId === partner.PartnerId
        )?.PbmEmail,
      }))
  }, [
    usedFlags,
    dataForTopPartners,
    filtersFormData.requestStatus,
    pbmAssignments,
    requestsForTabSet,
  ])

  const activeFlagRequests = useMemo(
    () => preparedPartners.filter((partner) => isActiveFlagRequest(partner.RequestStatus)),
    [preparedPartners]
  )

  const tabStats = useMemo(
    () =>
      new Map(
        flagTabs.map((tab) => {
          const flagged = preparedPartners.filter((partner) =>
            tab.flags.some(
              (flag) => partner.RTM === flag.RTM && hasFlag(partner.BitClass, flag.BitValue)
            )
          ).length
          const added = activeFlagRequests.filter((partner) =>
            tab.flags.some(
              (flag) =>
                partner.RTM === flag.RTM && getFlagRequestDirection(partner, flag) === 'added'
            )
          ).length
          const removed = activeFlagRequests.filter((partner) =>
            tab.flags.some(
              (flag) =>
                partner.RTM === flag.RTM && getFlagRequestDirection(partner, flag) === 'removed'
            )
          ).length

          return [tab.key, { flagged, added, removed }] as const
        })
      ),
    [flagTabs, preparedPartners, activeFlagRequests]
  )

  return (
    <div className={styles.root}>
      <TopPartnersManagementFilterArea
        partners={partners}
        modifiedFields={modifiedFields}
        initialFormData={initialFormData}
        formKey={formKey}
      />
      <TopPartnersManagementCard className={styles.tabsCard}>
        <Tabs
          selectedItem={activeTab}
          onItemSelect={setSelectedTab}
          classes={{
            header: styles.tabHeader,
            items: styles.tabItems,
            item: styles.tabItem,
            active: styles.active,
            content: styles.content,
          }}
        >
          {flagTabs.map((tab) => {
            const { flagged, added, removed } = tabStats.get(tab.key) ?? {
              flagged: 0,
              added: 0,
              removed: 0,
            }
            return (
              <Tabs.Item
                key={tab.key}
                title={
                  <div className={styles.tabLabel}>
                    <div className={styles.tabIcon}>
                      {tab.flags[0] && (
                        <RankIcon
                          partnerFlag={tab.flags[0].PartnerFlag}
                          rankConfig={tab.flags[0]}
                        />
                      )}
                    </div>
                    <div className={styles.tabTitle}>
                      <span className={styles.tabName} title={tab.title}>
                        {tab.title}
                      </span>
                      <span className={styles.counts}>
                        <strong>{flagged}</strong>
                        <Translation span text={TRANSLATION_KEYS.topPartners.tabs.flagged} />
                        <RenderWhen condition={added > 0 || removed > 0}>
                          <span className={styles.added}>+{added}</span>
                          <span className={styles.removed}>−{removed}</span>
                        </RenderWhen>
                      </span>
                    </div>
                  </div>
                }
              >
                <div className={styles.panel}>
                  <RequestsStatisticFilter
                    defaultValue={initialFormData.requestStatus}
                    field="requestStatus"
                    formKey={formKey}
                    partners={requestsForTab}
                  />
                  <TopPartnersManagementChart
                    key={tab.key}
                    data={dataForTopPartners}
                    selectedFlags={usedFlags}
                    allFlags={usedFlags}
                    flagConfigs={flagConfigs}
                  />
                  <TopPartnersManagementTable
                    partnerFlags={partnerFlags}
                    partnersForTable={filteredDataForTable}
                    fYear={fYear}
                    isPbm={usedFlags[0]?.RTM !== 'Distributor'}
                    topFlags={usedFlags}
                    allPartners={partners.filter((partner) => partner.RTM === usedFlags[0]?.RTM)}
                    setPartners={setPartners}
                    approvers={approvers}
                  />
                </div>
              </Tabs.Item>
            )
          })}
        </Tabs>
      </TopPartnersManagementCard>
    </div>
  )
}
